import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { assertNever } from 'owa-assert';
import {
    COMPOSE_ONLY_ATTACHMENT_CONTENT_END,
    COMPOSE_ONLY_ATTACHMENT_CONTENT_START,
} from 'owa-attachment-link-common';
import { getPackageBaseUrl } from 'owa-config';
import { DATA_OG_STYLE_COLOR } from 'owa-content-colors';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { LinkActionStatus, RecipientContainer, SharingLinkInfo } from 'owa-link-data';
import { format } from 'owa-localize';
import * as React from 'react';
import { Editor } from 'roosterjs-editor-core';
import { PositionType } from 'roosterjs-editor-types';
import ComposeLinkPreviewMode from '../store/schema/ComposeLinkPreviewMode';
import ComposeLinkViewState from '../store/schema/ComposeLinkViewState';
import {
    OWA_AUTO_LINKS_CLASSNAME,
    SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME,
    SPAN_TAG_NAME,
} from '../utils/constants';
import getLinkPreviewMode from '../utils/getLinkPreviewMode';
import { getHasSharingIssues } from '../utils/link/getHasSharingIssues';
import { SharingIssueLevel } from '../utils/link/SharingIssueLevel';
import styles from './Link.scss';
import { showLinkContextMenu } from './LinkContextMenu';
import ReactComment from './ReactComment';

const CONTEXT_MENU_EVENT_NAME = 'contextmenu';
const CLICK_EVENT_NAME = 'click';
const ATTRIBUTE_STYLE_NAME = 'style';
const ATTRIBUTE_CONTENTEDITABLE_NAME = 'contenteditable';
const STYLE_SEPARATOR = ';';

interface SharingLinkProps {
    viewState: ComposeLinkViewState;
    getRecipientWells: () => RecipientContainer[];
    fromAddress: string;
    sharingLink: SharingLinkInfo;
    fileIcon: string;
    hasValidFileName: boolean;
    editor: Editor;
    originalAnchor: HTMLAnchorElement;
    composeId: string;
    isLinkPreviouslyBeautified: boolean;
}

const linkStyles = require('./Link.scss');
const classNames = require('classnames/bind').bind(linkStyles);

@observer
export class Link extends React.Component<SharingLinkProps, {}> {
    private link: HTMLAnchorElement;
    private linkPreviewMode: ComposeLinkPreviewMode;

    render() {
        const sharingIssueLevel: SharingIssueLevel = this.props.viewState
            ? getHasSharingIssues(
                  this.props.viewState.linkId,
                  this.props.viewState.isLinkBeautified,
                  this.props.getRecipientWells,
                  this.props.fromAddress
              )
            : SharingIssueLevel.None;

        if (isFeatureEnabled('doc-SxS-jsApi-ODB-Links')) {
            this.linkPreviewMode = getLinkPreviewMode(this.props.sharingLink.linkId);
        } else {
            this.linkPreviewMode = ComposeLinkPreviewMode.None;
        }

        const conditionalStyles = {
            attachingAsACopy:
                this.props.sharingLink.linkActionStatus === LinkActionStatus.attachingAsACopy ||
                this.props.sharingLink.linkActionStatus === LinkActionStatus.pendingAttachAsACopy,
            link: this.props.viewState.isLinkBeautified,
        };

        conditionalStyles[this.getComposeLinkStyle(this.linkPreviewMode)] = true;
        return (
            <a
                ref={ref => (this.link = ref)}
                href={this.props.sharingLink.url}
                onMouseOver={this.onMouseOver}
                onMouseLeave={this.onMouseLeave}
                style={this.getAnchorStyle(sharingIssueLevel)}
                id={this.props.sharingLink.linkId}
                className={classNames(
                    OWA_AUTO_LINKS_CLASSNAME,
                    this.getAnchorTextColor(sharingIssueLevel),
                    conditionalStyles
                )}>
                {this.renderEditIcon()}
                {this.props.viewState.isLinkBeautified && this.props.hasValidFileName && (
                    <img
                        src={this.props.fileIcon}
                        style={this.getFileIconStyle()}
                        alt=""
                        role="presentation"
                    />
                )}
                {this.props.viewState.displayName}
                {this.renderErrorIcon(sharingIssueLevel)}
            </a>
        );
    }

    componentDidMount() {
        if (this.props.editor.isDisposed()) {
            return;
        }

        this.copyStyleFromOriginalAnchor();

        // Make beautiful link readonly & handle the click event
        if (this.props.viewState.isLinkBeautified) {
            this.link.setAttribute(ATTRIBUTE_CONTENTEDITABLE_NAME, 'false');
        }

        // For dark mode link with sharing issues, the inline css will be removed during mail send after we set this attribute
        this.link.setAttribute(DATA_OG_STYLE_COLOR, '');

        this.link.addEventListener(CLICK_EVENT_NAME, this.onClick);
        this.link.addEventListener(CONTEXT_MENU_EVENT_NAME, this.onContextMenu);

        // We need to replace the child only so we keep any text typed under the parent element before the new anchor
        this.props.editor.replaceNode(this.props.originalAnchor, this.link);

        // When it's a newly pasted link, guarantee the cursor is after the pasted link. If opening a draft, do not set the cursor location.
        const selectionRange = this.props.editor.getSelectionRange();
        if (selectionRange) {
            if (
                selectionRange.startContainer === selectionRange.endContainer &&
                selectionRange.startContainer === this.link.parentNode
            ) {
                this.props.editor.select(this.link, PositionType.After);
            }
        }
    }

    componentWillUnmount() {
        if (this.link) {
            this.link.removeEventListener(CLICK_EVENT_NAME, this.onClick);
            this.link.removeEventListener(CONTEXT_MENU_EVENT_NAME, this.onContextMenu);
        }
    }

    private copyStyleFromOriginalAnchor() {
        // Pass the style from old link to new link
        let style: string = this.props.originalAnchor.getAttribute(ATTRIBUTE_STYLE_NAME);
        if (style && style.length > 0 && style.lastIndexOf(STYLE_SEPARATOR) !== style.length - 1) {
            style += STYLE_SEPARATOR;
        }
        style += this.link.getAttribute(ATTRIBUTE_STYLE_NAME);

        // If the original anchor is a beautiful link, we need to check whether it has a span defining the font-size.
        if (this.props.isLinkPreviouslyBeautified) {
            const spanElement = this.props.originalAnchor.querySelector(SPAN_TAG_NAME);
            if (spanElement) {
                const spanStyle = this.props.originalAnchor.children[1].getAttribute(
                    ATTRIBUTE_STYLE_NAME
                );
                style += spanStyle;
            }
        }

        this.link.setAttribute(ATTRIBUTE_STYLE_NAME, style);

        const originalSrc: string = this.props.originalAnchor.getAttribute(
            SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME
        );
        if (originalSrc && originalSrc.length > 0) {
            this.link.setAttribute(SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME, originalSrc);
        }
    }

    private renderEditIcon() {
        if (this.props.viewState.isLinkBeautified && isFeatureEnabled('doc-SxS-jsApi-ODB-Links')) {
            return (
                <React.Fragment>
                    <ReactComment text={COMPOSE_ONLY_ATTACHMENT_CONTENT_START} />
                    <Icon
                        className={
                            this.linkPreviewMode === ComposeLinkPreviewMode.Edit
                                ? linkStyles.editLinkIcon
                                : linkStyles.hidden
                        }
                        iconName={ControlIcons.Edit}
                    />
                    <ReactComment text={COMPOSE_ONLY_ATTACHMENT_CONTENT_END} />
                </React.Fragment>
            );
        } else {
            return null;
        }
    }

    // Error Icon is only shown in beautiful link
    // In non-beautiful link, we just change the color of link when it has sharing issue.
    private renderErrorIcon(sharingIssueLevel: SharingIssueLevel) {
        if (this.props.viewState.isLinkBeautified) {
            return (
                <React.Fragment>
                    <ReactComment text={COMPOSE_ONLY_ATTACHMENT_CONTENT_START} />
                    <img
                        alt=""
                        role="presentation"
                        src={this.getErrorIcon(sharingIssueLevel)}
                        hidden={sharingIssueLevel === SharingIssueLevel.None}
                        style={this.getErrorIconStyle()}
                    />
                    <ReactComment text={COMPOSE_ONLY_ATTACHMENT_CONTENT_END} />
                </React.Fragment>
            );
        } else {
            return null;
        }
    }

    private onContextMenu = (event: MouseEvent) => {
        showLinkContextMenu(
            this.link,
            this.props.editor,
            this.props.composeId,
            this.props.getRecipientWells(),
            this.props.fromAddress
        );
        event.preventDefault();
    };

    private onClick = (event: MouseEvent) => {
        showLinkContextMenu(
            this.link,
            this.props.editor,
            this.props.composeId,
            this.props.getRecipientWells(),
            this.props.fromAddress
        );
        event.preventDefault();
    };

    private getAnchorStyle = (sharingIssueLevel: SharingIssueLevel): React.CSSProperties => {
        if (this.props.editor.isDisposed()) {
            return null;
        }

        let style: React.CSSProperties = {};
        if (this.props.viewState.isLinkBeautified) {
            // Highlighted Background
            style = {
                padding: '0 1px',
                backgroundColor: 'rgb(244,244,244)',
                borderRadius: '2px',
                userSelect: 'all',
            };
        }

        if (sharingIssueLevel === SharingIssueLevel.Error && this.props.editor.isDarkMode()) {
            style = {
                ...style,
                color: styles.linkErrorColor,
            };
        }

        return style;
    };

    private getFileIconStyle() {
        return {
            width: '16px',
            height: '16px',
            verticalAlign: 'middle',
            padding: '1px 2px 2px 0',
        };
    }

    private getErrorIconStyle() {
        return {
            width: '12px',
            height: '12px',
            verticalAlign: 'top',
        };
    }

    private getAnchorTextColor = (sharingIssueLevel: SharingIssueLevel) => {
        if (this.props.editor.isDisposed()) {
            return null;
        }

        if (sharingIssueLevel === SharingIssueLevel.Error && !this.props.editor.isDarkMode()) {
            return linkStyles.linkWithSharingIssue;
        }
    };

    private getErrorIcon(shairngIssueLevel: SharingIssueLevel) {
        if (shairngIssueLevel === SharingIssueLevel.Error) {
            return format('{0}{1}', getPackageBaseUrl(), 'resources/images/redinfo_24.png');
        } else if (shairngIssueLevel === SharingIssueLevel.Warning) {
            return format('{0}{1}', getPackageBaseUrl(), 'resources/images/greyinfo_24.png');
        }
        return '';
    }

    private getComposeLinkStyle(composeLinkPreviewMode: ComposeLinkPreviewMode): string {
        switch (composeLinkPreviewMode) {
            case ComposeLinkPreviewMode.Edit:
                return 'edit';
            case ComposeLinkPreviewMode.Selected:
                return 'selected';
            case ComposeLinkPreviewMode.None:
                return 'none';
            default:
                return assertNever(composeLinkPreviewMode);
        }
    }

    /*
    We have to use inline CSS style: background-color for it to work in different clients such as gmail.
    Since inline CSS style has higher preference than class, we can't use :hover as it would be just ignored.
    So we have to hook to these two events to change the hover background color.
    */

    private onMouseOver = () => {
        if (this.link && this.props.viewState.isLinkBeautified) {
            this.link.style.backgroundColor = 'rgb(237,235,233)';
        }
    };

    private onMouseLeave = () => {
        if (this.link && this.props.viewState.isLinkBeautified) {
            this.link.style.backgroundColor = 'rgb(243,242,241)';
        }
    };
}
